
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { SupplierModel, ISupplierProductDisplay } from '@/models/supplier';
import { IPurchaseOrderItem } from '@/models/purchase_order';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { EventBus } from '@/main';

@Component
export default class AddProductsDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly dialog!: boolean;
  @Prop({ type: Number, default: null }) readonly supplierId!: number | null;
  @Prop({ type: String, default: '' }) readonly supplierName!: string;
  @Prop({ type: Array, default: () => [] }) readonly existingItems!: IPurchaseOrderItem[];

  private searchQuery = '';
  private selectedProducts: ISupplierProductDisplay[] = [];
  private availableProducts: ISupplierProductDisplay[] = [];
  private loading = false;
  private adding = false;
  private totalProducts = 0;
  private supplier: SupplierModel | null = null;
  private searchTimeout: number | null = null;
  private fetchError = '';

  private tableOptions: any = {
    page: 1,
    itemsPerPage: 25,
    sortBy: [],
    sortDesc: [],
  };

  private headers = [
    { text: '', value: 'image', sortable: false, width: '56px' },
    { text: 'Product', value: 'productDetails', sortable: false },
    { text: 'Unit Cost', value: 'unit_cost', sortable: true, width: '120px', align: 'right' },
  ];

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (val) {
      this.resetState();
      this.initSupplier();
    }
  }

  @Watch('tableOptions', { deep: true })
  onTableOptionsChange() {
    if (this.dialog && this.supplier) {
      this.fetchProducts();
    }
  }

  @Watch('searchQuery')
  onSearchQueryChange() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = window.setTimeout(() => {
      this.fetchProducts(true);
    }, 500);
  }

  private resetState() {
    this.supplier = null;
    this.searchQuery = '';
    this.selectedProducts = [];
    this.availableProducts = [];
    this.totalProducts = 0;
    this.loading = false;
    this.adding = false;
    this.fetchError = '';
    this.tableOptions = {
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: [],
    };
  }

  private async initSupplier() {
    if (!this.supplierId) return;

    this.fetchError = '';
    this.loading = true;
    try {
      this.supplier = await SupplierModel.getById(this.supplierId);
      await this.fetchProducts();
    } catch (error: any) {
      this.fetchError = `Failed to load supplier products: ${error.message || 'Unknown error'}`;
      console.error('Error loading supplier:', error);
    } finally {
      this.loading = false;
    }
  }

  private async fetchProducts(resetPage = false) {
    if (!this.supplier) return;

    if (resetPage) {
      this.tableOptions.page = 1;
    }

    this.loading = true;
    try {
      const result = await this.supplier.fetchProductsOptimized({
        page: this.tableOptions.page,
        limit: this.tableOptions.itemsPerPage,
        search: this.searchQuery || '',
        sortBy: this.tableOptions.sortBy?.[0] || '',
        sortOrder: this.tableOptions.sortDesc?.[0] ? 'desc' : 'asc',
      });

      // Exclude products already in the PO
      const existingVariantIds = new Set(this.existingItems.map(item => Number(item.variantId)));
      const unfilteredCount = result.products.length;
      this.availableProducts = result.products.filter(
        p => !existingVariantIds.has(Number(p.variant_id))
      );
      const filteredOut = unfilteredCount - this.availableProducts.length;
      this.totalProducts = Math.max(0, result.pagination.total - filteredOut);
      this.fetchError = '';
    } catch (error: any) {
      this.fetchError = `Failed to fetch products: ${error.message || 'Unknown error'}`;
      EventBus.$emit('show-snackbar', this.fetchError, 'error');
      console.error('Error fetching supplier products:', error);
    } finally {
      this.loading = false;
    }
  }

  private onSearchClear() {
    this.searchQuery = '';
    // The @Watch('searchQuery') watcher handles the fetch via debounce
  }

  private formatCurrency(value: number): string {
    return CurrencyHelper.formatCurrency(value);
  }

  private addSelectedProducts() {
    if (this.selectedProducts.length === 0) return;

    // Convert supplier products to PO item format
    const newItems: IPurchaseOrderItem[] = this.selectedProducts.map(product => ({
      variantId: Number(product.variant_id),
      variantImage: product.image || '',
      productName: product['Item[Item Description]'] || '',
      variant: product.variant || '',
      sku: product.product_sku || '',
      supplierSku: product.supplier_sku || '',
      orderedQuantity: product['[min_order_quantity]'] || 1,
      receivedQuantity: 0,
      unitPrice: product.unit_cost || 0,
      totalPrice: (product['[min_order_quantity]'] || 1) * (product.unit_cost || 0),
      _action: 'add',
    }));

    this.$emit('products-added', newItems);
    this.closeDialog();
  }

  private closeDialog() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.$emit('update:dialog', false);
  }

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
}
